import TextButton from 'LuxKit/Button/TextButton'
import React, { forwardRef, useCallback, useContext, useEffect } from 'react'
import BusinessTravellerAccountMenuContent from './BusinessTravellerAccountMenuContent'
import ModalContext from 'contexts/ModalContext'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import { connect } from 'react-redux'
import GeoContext from 'contexts/geoContext'
import useBusinessTravellerTripRequestApprovals from 'businessTraveller/hooks/useBusinessTravellerTripRequestApprovals'
import useBusinessTravellerTripRequests from 'businessTraveller/hooks/useBusinessTravellerTripRequests'
import Badge from 'LuxKit/Badge'
import { BUSINESS_TRAVELLER_MY_APPROVALS_PENDING_ROUTE } from 'components/App/Routes/routeConstants'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isBusinessTravellerTripRequestApprover } from 'businessTraveller/selectors/isBusinessTravellerTripRequestApprover'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'

interface Props {
  className?: string
  balance?: number
}

const BusinessTravellerAccountButtonMenu = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { balance } = props
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)

  useEffect(() => {
    dispatch(fetchAccountBusiness())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const employeeId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.employee?.id)
  const isBusinessTravellerApprover = useAppSelector(isBusinessTravellerTripRequestApprover)
  const { pending: pendingApprovals } = useBusinessTravellerTripRequestApprovals({ currentRegionCode })
  const { approved: approvedTripRequests } = useBusinessTravellerTripRequests({ currentRegionCode, isEnabled: !!employeeId })

  const shouldShowIndicator = Boolean(pendingApprovals?.length && pendingApprovals?.length > 0)
  const hasApprovedUnbookedTripRequests = (approvedTripRequests?.length ?? 0) > 0

  const openAccountDrawer = useCallback(() => {
    showModal(<BusinessTravellerAccountMenuContent />)
  }, [showModal])

  return <Group direction="horizontal" ref={ref}>
    {isBusinessTravellerApprover && <TextButton
      kind="primary"
      variant="ghost"
      to={BUSINESS_TRAVELLER_MY_APPROVALS_PENDING_ROUTE}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        My Approvals
        {shouldShowIndicator && (
          <Badge
            kind="string"
            mode="critical"
          >
            {pendingApprovals?.length}
          </Badge>
        )}
      </span>
    </TextButton>}
    <TextButton
      kind="primary"
      variant="ghost"
      to="/account/my-bookings"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        My Bookings
        {hasApprovedUnbookedTripRequests && (
          <Badge kind="dot" mode="critical" />
        )}
      </span>
    </TextButton>
    <TextButton
      kind="tertiary"
      variant="dark"
      startIcon={<LineBuildingIcon />}
      onClick={openAccountDrawer}
    >
      <div>
        Account
        {!!balance && <Caption variant="small">
          <FormatCurrency value={balance} format="dynamicDollar" />
        </Caption>}
      </div>
    </TextButton>
  </Group>
})

function mapStateToProps(state: App.State) {
  return {
    balance: state.auth.account.balance,
  }
}

export default connect(mapStateToProps)(BusinessTravellerAccountButtonMenu)
